import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { sales as salesApi } from "../services/api";
import {
  formatDate,
  formatDateForInput,
  formatCurrency,
} from "../utils/dateUtils";
import {
  BackIcon,
  ShoppingBagIcon,
  UndoIcon,
  PencilIcon,
} from "../components/Icons";
import Modal, { ConfirmModal } from "../components/Modal";
import { toast } from "../components/Toast";
import DataTable, { RowActions } from "../components/DataTable";

const PAGE_SIZE = 50;

function ItemsSold() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  // Summary totals come from /sales/stats so they cover ALL sales, not just
  // the pages loaded into the table.
  const [stats, setStats] = useState({
    totalSales: 0,
    totalRevenue: 0,
    totalProfit: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [editingSale, setEditingSale] = useState(null);
  const [undoingSale, setUndoingSale] = useState(null);
  const [editForm, setEditForm] = useState({
    sale_price: "",
    purchase_price: "",
    fees: "",
    channel: "",
    notes: "",
    sale_date: "",
  });

  const loadSales = useCallback(async () => {
    try {
      setLoading(true);
      const [listRes, statsRes] = await Promise.all([
        salesApi.list({ limit: PAGE_SIZE, offset: 0 }),
        salesApi.stats(),
      ]);
      setItems(listRes.data);
      setHasMore(listRes.data.length === PAGE_SIZE);
      setStats(statsRes.data);
      setError(null);
    } catch (err) {
      console.error("Failed to load sales:", err);
      setError("Couldn't load sales");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMoreSales = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const { data } = await salesApi.list({
        limit: PAGE_SIZE,
        offset: items.length,
      });
      setItems((prev) => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    } catch (err) {
      console.error("Failed to load more sales:", err);
      toast.error("Failed to load more sales");
    } finally {
      setLoadingMore(false);
    }
  }, [items.length, loadingMore, hasMore]);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  const handleUndoSale = async () => {
    if (!undoingSale) return;
    try {
      await salesApi.delete(undoingSale.id);
      setUndoingSale(null);
      loadSales();
    } catch (err) {
      console.error("Failed to undo sale:", err);
      toast.error("Failed to undo sale");
    }
  };

  const handleEditClick = (sale) => {
    setEditingSale(sale);
    setEditForm({
      sale_price: sale.sale_price?.toString() || "",
      purchase_price: sale.purchase_price?.toString() || "",
      fees: sale.fees?.toString() || "0",
      channel: sale.channel || "",
      notes: sale.notes || "",
      sale_date: formatDateForInput(sale.sale_date),
    });
  };

  const handleEditSave = async () => {
    try {
      await salesApi.update(editingSale.id, {
        sale_price: parseFloat(editForm.sale_price) || 0,
        purchase_price: parseFloat(editForm.purchase_price) || 0,
        fees: parseFloat(editForm.fees) || 0,
        channel: editForm.channel || null,
        notes: editForm.notes || null,
        sale_date: editForm.sale_date || null,
      });
      setEditingSale(null);
      loadSales();
    } catch (err) {
      console.error("Failed to update sale:", err);
      toast.error("Failed to update sale");
    }
  };

  // Column definitions for DataTable
  const columns = [
    {
      field: "entity_name",
      header: "Item",
      render: (_, item) =>
        item.entity_name || item.snapshot?.name || "Unknown Item",
      getSortValue: (item) =>
        (item.entity_name || item.snapshot?.name || "").toLowerCase(),
    },
    {
      field: "channel",
      header: "Channel",
      render: (value) =>
        value ? (
          <span className="badge">{value}</span>
        ) : (
          <span className="text-muted">-</span>
        ),
    },
    {
      field: "sale_price",
      header: "Sale Price",
      align: "right",
      render: (value) => formatCurrency(value),
      getSortValue: (item) => parseFloat(item.sale_price) || 0,
    },
    {
      field: "profit",
      header: "Profit",
      align: "right",
      render: (value) => (
        <span
          style={{
            color:
              value >= 0 ? "var(--accent-success)" : "var(--accent-danger)",
          }}
        >
          {formatCurrency(value)}
        </span>
      ),
      getSortValue: (item) => parseFloat(item.profit) || 0,
    },
    {
      field: "sale_date",
      header: "Date Sold",
      render: (value) => (
        <span style={{ color: "var(--text-secondary)" }}>
          {formatDate(value)}
        </span>
      ),
      getSortValue: (item) => new Date(item.sale_date).getTime(),
    },
  ];

  // Search filter function
  const searchFilter = (item, query) => {
    const q = query.toLowerCase();
    const name = item.entity_name || item.snapshot?.name || "";
    return (
      name.toLowerCase().includes(q) || item.channel?.toLowerCase().includes(q)
    );
  };

  // Row actions renderer
  const rowActions = (item) => (
    <RowActions
      actions={[
        {
          label: "Edit",
          icon: PencilIcon,
          onClick: () => handleEditClick(item),
        },
        {
          label: "Undo Sale",
          icon: UndoIcon,
          onClick: () => setUndoingSale(item),
          variant: "danger",
        },
      ]}
    />
  );

  return (
    <div style={{ minHeight: "100vh", paddingBottom: "80px" }}>
      <div className="header">
        <div className="header-content">
          <button
            onClick={() => navigate("/")}
            className="btn btn-ghost btn-icon"
          >
            <BackIcon size={20} />
          </button>
          <div className="header-text">
            <h1 className="header-title">Items Sold</h1>
            <p className="header-subtitle">
              {stats.totalSales} item{stats.totalSales !== 1 ? "s" : ""} sold
            </p>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Summary Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: "var(--spacing-md)",
            marginBottom: "var(--spacing-lg)",
          }}
        >
          <div className="card" style={{ padding: "var(--spacing-lg)" }}>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-muted)",
                textTransform: "uppercase",
                marginBottom: "var(--spacing-xs)",
              }}
            >
              Total Revenue
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700" }}>
              {formatCurrency(stats.totalRevenue)}
            </div>
          </div>
          <div className="card" style={{ padding: "var(--spacing-lg)" }}>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-muted)",
                textTransform: "uppercase",
                marginBottom: "var(--spacing-xs)",
              }}
            >
              Total Profit
            </div>
            <div
              style={{
                fontSize: "1.5rem",
                fontWeight: "700",
                color:
                  stats.totalProfit >= 0
                    ? "var(--accent-success)"
                    : "var(--accent-danger)",
              }}
            >
              {formatCurrency(stats.totalProfit)}
            </div>
          </div>
          <div className="card" style={{ padding: "var(--spacing-lg)" }}>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-muted)",
                textTransform: "uppercase",
                marginBottom: "var(--spacing-xs)",
              }}
            >
              Items Sold
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700" }}>
              {stats.totalSales}
            </div>
          </div>
        </div>

        {/* Data Table — sorting is client-side over the loaded rows */}
        {error && items.length === 0 ? (
          <div
            className="card text-center"
            style={{ padding: "var(--spacing-2xl)" }}
          >
            <p
              style={{
                marginBottom: "var(--spacing-md)",
                color: "var(--text-secondary)",
              }}
            >
              {error}
            </p>
            <button onClick={loadSales} className="btn btn-primary">
              Retry
            </button>
          </div>
        ) : (
          <DataTable
            data={items}
            loading={loading}
            columns={columns}
            searchable
            searchPlaceholder="Search sold items..."
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchFilter={searchFilter}
            defaultSortField="sale_date"
            defaultSortDirection="desc"
            rowActions={rowActions}
            hasMore={hasMore}
            onLoadMore={loadMoreSales}
            emptyIcon={ShoppingBagIcon}
            emptyTitle="No items sold yet"
            emptySubtitle="Items you mark as sold will appear here"
          />
        )}

        {/* Edit Sale Modal */}
        <Modal
          isOpen={!!editingSale}
          onClose={() => setEditingSale(null)}
          onSubmit={handleEditSave}
          title="Edit Sale"
          footer={
            <>
              <button
                onClick={() => setEditingSale(null)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button onClick={handleEditSave} className="btn btn-primary">
                Save Changes
              </button>
            </>
          }
        >
          {editingSale && (
            <>
              <p
                style={{
                  marginBottom: "var(--spacing-md)",
                  color: "var(--text-secondary)",
                }}
              >
                {editingSale.entity_name ||
                  editingSale.snapshot?.name ||
                  "Unknown Item"}
              </p>

              <div className="grid grid-2" style={{ gap: "var(--spacing-md)" }}>
                <div>
                  <label>Sale Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.sale_price}
                    onChange={(e) =>
                      setEditForm({ ...editForm, sale_price: e.target.value })
                    }
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label>Purchase Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.purchase_price}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        purchase_price: e.target.value,
                      })
                    }
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div
                className="grid grid-2"
                style={{
                  gap: "var(--spacing-md)",
                  marginTop: "var(--spacing-md)",
                }}
              >
                <div>
                  <label>Fees</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.fees}
                    onChange={(e) =>
                      setEditForm({ ...editForm, fees: e.target.value })
                    }
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label>Sale Date</label>
                  <input
                    type="date"
                    value={editForm.sale_date}
                    onChange={(e) =>
                      setEditForm({ ...editForm, sale_date: e.target.value })
                    }
                  />
                </div>
              </div>

              <div style={{ marginTop: "var(--spacing-md)" }}>
                <label>Channel</label>
                <input
                  type="text"
                  value={editForm.channel}
                  onChange={(e) =>
                    setEditForm({ ...editForm, channel: e.target.value })
                  }
                  placeholder="e.g., eBay, Facebook, OfferUp"
                />
              </div>

              <div style={{ marginTop: "var(--spacing-md)" }}>
                <label>Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) =>
                    setEditForm({ ...editForm, notes: e.target.value })
                  }
                  rows={3}
                  placeholder="Optional notes about this sale"
                />
              </div>
            </>
          )}
        </Modal>

        {/* Undo Sale Confirm Modal */}
        <ConfirmModal
          isOpen={!!undoingSale}
          onClose={() => setUndoingSale(null)}
          onConfirm={handleUndoSale}
          title="Undo Sale"
          message={
            undoingSale && (
              <p>
                Undo sale of "
                <strong>
                  {undoingSale.entity_name ||
                    undoingSale.snapshot?.name ||
                    "this item"}
                </strong>
                "? This will restore the item to your inventory.
              </p>
            )
          }
          confirmText="Undo Sale"
          confirmVariant="danger"
        />
      </div>
    </div>
  );
}

export default ItemsSold;
